'use client'

import React, { createContext, useContext, useReducer, useCallback } from 'react';

interface PanelState {
  isClosed: boolean;
  isOpen: boolean;
  isMinimized: boolean;
  zIndex: number;
  order: number;
  position?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

interface PanelStore {
  panels: Record<string, PanelState>;
  activePanel: string | null;
  nextOrder: number;
}

type PanelAction =
  | { type: 'OPEN_PANEL'; panelId: string; position?: PanelState['position'] }
  | { type: 'CLOSE_PANEL'; panelId: string }
  | { type: 'MINIMIZE_PANEL'; panelId: string }
  | { type: 'RESTORE_PANEL'; panelId: string }
  | { type: 'BRING_TO_FRONT'; panelId: string }
  | { type: 'UPDATE_POSITION'; panelId: string; position: PanelState['position'] };

const initialState: PanelStore = {
  panels: {},
  activePanel: null,
  nextOrder: 1,
};

const BASE_Z_INDEX = 1000;

const panelReducer = (state: PanelStore, action: PanelAction): PanelStore => {
  console.log('Panel action:', action.type, action);
  
  switch (action.type) {
    case 'OPEN_PANEL':
      return {
        ...state,
        panels: {
          ...state.panels,
          [action.panelId]: {
            isClosed: false,
            isOpen: true,
            isMinimized: false,
            zIndex: BASE_Z_INDEX + state.nextOrder,
            order: state.nextOrder,
            position: action.position,
          },
        },
        activePanel: action.panelId,
        nextOrder: state.nextOrder + 1,
      };

    case 'CLOSE_PANEL':
      const { [action.panelId]: _, ...remainingPanels } = state.panels;
      return {
        ...state,
        panels: remainingPanels,
        activePanel: state.activePanel === action.panelId ? null : state.activePanel,
      };

    case 'MINIMIZE_PANEL':
      return {
        ...state,
        panels: {
          ...state.panels,
          [action.panelId]: {
            ...state.panels[action.panelId],
            isMinimized: true,
          },
        },
      };

    case 'RESTORE_PANEL':
      return {
        ...state,
        panels: {
          ...state.panels,
          [action.panelId]: {
            ...state.panels[action.panelId],
            isMinimized: false,
          },
        },
      };

    case 'BRING_TO_FRONT':
      return {
        ...state,
        panels: {
          ...state.panels,
          [action.panelId]: {
            ...state.panels[action.panelId],
            zIndex: BASE_Z_INDEX + state.nextOrder,
            order: state.nextOrder,
          },
        },
        activePanel: action.panelId,
        nextOrder: state.nextOrder + 1,
      };

    case 'UPDATE_POSITION':
      return {
        ...state,
        panels: {
          ...state.panels,
          [action.panelId]: {
            ...state.panels[action.panelId],
            position: action.position,
          },
        },
      };

    default:
      return state;
  }
};

interface PanelContextValue {
  state: PanelStore;
  openPanel: (panelId: string, position?: PanelState['position']) => void;
  closePanel: (panelId: string) => void;
  minimizePanel: (panelId: string) => void;
  restorePanel: (panelId: string) => void;
  bringToFront: (panelId: string) => void;
  updatePosition: (panelId: string, position: PanelState['position']) => void;
  getPanelState: (panelId: string) => PanelState | undefined;
}

const PanelContext = createContext<PanelContextValue | null>(null);

export const PanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(panelReducer, initialState);

  const openPanel = useCallback((panelId: string, position?: PanelState['position']) => {
    dispatch({ type: 'OPEN_PANEL', panelId, position });
  }, []);

  const closePanel = useCallback((panelId: string) => {
    dispatch({ type: 'CLOSE_PANEL', panelId });
  }, []);

  const minimizePanel = useCallback((panelId: string) => {
    dispatch({ type: 'MINIMIZE_PANEL', panelId });
  }, []);

  const restorePanel = useCallback((panelId: string) => {
    dispatch({ type: 'RESTORE_PANEL', panelId });
  }, []);

  const bringToFront = useCallback((panelId: string) => {
    dispatch({ type: 'BRING_TO_FRONT', panelId });
  }, []);

  const updatePosition = useCallback((panelId: string, position: PanelState['position']) => {
    dispatch({ type: 'UPDATE_POSITION', panelId, position });
  }, []);

  const getPanelState = useCallback((panelId: string) => {
    return state.panels[panelId];
  }, [state.panels]);

  return (
    <PanelContext.Provider
      value={{
        state,
        openPanel,
        closePanel,
        minimizePanel,
        restorePanel,
        bringToFront,
        updatePosition,
        getPanelState,
      }}
    >
      {children}
    </PanelContext.Provider>
  );
};

export const usePanel = () => {
  const context = useContext(PanelContext);
  if (!context) {
    throw new Error('usePanel must be used within a PanelProvider');
  }
  return context;
};